import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { identityTranslator, loadTranslator, TranslateFn } from './gettext';

/**
 * Sdílený translator pro React komponenty — čte STEJNÉ gettext katalogy jako
 * NetteTranslator (viz ./gettext.ts). V komponentách se používají tytéž msgid
 * řetězce jako v Latte {_'...'}, žádné duplikované slovníky.
 *
 * Použití:
 *   <I18nProvider url={props.translationsUrl}> ... </I18nProvider>
 *   const t = useI18n();  →  t('Kontaktní údaje')  /  t('%d položek', count)
 */

const I18nContext = createContext<TranslateFn>(identityTranslator);

interface Props {
    /** URL .mo katalogu (s ?v= verzí kvůli cache); NULL/prázdné = bez překladu */
    url: string | null;
    children: ReactNode;
    /** co vykreslit během načítání katalogu (default nic — zabrání probliknutí msgid) */
    loading?: ReactNode;
}

export function I18nProvider({ url, children, loading = null }: Props) {
    const [translator, setTranslator] = useState<TranslateFn | null>(() => url ? null : identityTranslator);

    useEffect(() => {
        if (!url) {
            setTranslator(() => identityTranslator);
            return;
        }

        let cancelled = false;
        void loadTranslator(url).then((t) => {
            if (!cancelled) {
                setTranslator(() => t);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [url]);

    if (translator === null) {
        return <>{loading}</>;
    }

    return <I18nContext.Provider value={translator}>{children}</I18nContext.Provider>;
}

/** Překladová funkce z nejbližšího I18nProvider: t(msgid) / t(msgid, count). */
export function useI18n(): TranslateFn {
    return useContext(I18nContext);
}
